/**
 * @module components/intro
 *
 * The methodology section: the three pillars, the headline figures with
 * animated counters, and the four architecture domains.
 *
 * Everything rendered here comes from `data/site.js` and `data/phases.js`, so
 * the copy, the numbers and the labels have exactly one home.
 */

import { gsap, EASE, MEDIA, createMotionContext } from "../core/gsap-setup.js";
import { qs, qsa } from "../core/dom.js";
import { PILLARS, STATS } from "../data/site.js";
import { DOMAINS } from "../data/phases.js";
import { icon } from "../svg/icons.js";

/**
 * Render a single pillar.
 *
 * @param {import("../data/site.js").Pillar} pillar
 * @param {number} index
 * @returns {string} Markup.
 */
function pillarMarkup(pillar, index) {
  return `
    <li class="pillar card" data-pillar="${pillar.id}">
      <span class="card__index">${String(index + 1).padStart(2, "0")}</span>
      <span class="pillar__glyph" aria-hidden="true">${icon(pillar.glyph, { size: 22 })}</span>
      <h3 class="pillar__title">${pillar.title}</h3>
      <p class="pillar__body">${pillar.body}</p>
    </li>`;
}

/**
 * Render a single statistic. The numeric part is separated so it can be
 * counted up without disturbing the suffix.
 *
 * @param {import("../data/site.js").Stat} stat
 * @returns {string} Markup.
 */
function statMarkup(stat) {
  return `
    <div class="stat" data-stat>
      <dd class="stat__value">
        <span data-stat-value data-target="${stat.value}">0</span><span class="stat__suffix">${stat.suffix}</span>
      </dd>
      <dt class="stat__label">${stat.label}</dt>
      <dd class="stat__detail">${stat.detail}</dd>
    </div>`;
}

/**
 * Render a single architecture domain.
 *
 * @param {import("../data/phases.js").Domain} domain
 * @returns {string} Markup.
 */
function domainMarkup(domain) {
  return `
    <li class="domain" data-domain="${domain.id}">
      <span class="domain__code">${domain.code}</span>
      <span class="domain__name">${domain.name}</span>
      <span class="domain__body">${domain.body}</span>
    </li>`;
}

/**
 * @returns {() => void} Teardown.
 */
export function initIntro() {
  const root = qs("[data-intro]");
  if (!root) return () => {};

  /* --- Render ------------------------------------------------------------ */

  const pillarList = qs("[data-pillars]", root);
  if (pillarList) pillarList.innerHTML = PILLARS.map(pillarMarkup).join("");

  const statList = qs("[data-stats]", root);
  if (statList) statList.innerHTML = STATS.map(statMarkup).join("");

  const domainList = qs("[data-domains]", root);
  if (domainList) domainList.innerHTML = DOMAINS.map(domainMarkup).join("");

  /* --- Counters ---------------------------------------------------------- */

  const counters = qsa("[data-stat-value]", root);
  const mm = createMotionContext();

  /** Write the final value immediately — the reduced-motion and error path. */
  const settle = () => {
    counters.forEach((node) => {
      node.textContent = node.getAttribute("data-target") ?? node.textContent;
    });
  };

  mm.add(MEDIA.reduce, settle);

  mm.add(MEDIA.any, () => {
    const tweens = counters.map((node) => {
      const target = Number(node.getAttribute("data-target") ?? 0);
      const proxy = { value: 0 };

      // Start from zero so the count-up is always visible on first reveal.
      node.textContent = "0";

      return gsap.to(proxy, {
        value: target,
        duration: 1.8,
        ease: EASE.out,
        snap: { value: 1 },
        onUpdate: () => {
          node.textContent = String(Math.round(proxy.value));
        },
        scrollTrigger: { trigger: node, start: "top 92%", once: true },
      });
    });

    return () => {
      tweens.forEach((tween) => {
        tween.scrollTrigger?.kill();
        tween.kill();
      });
      settle();
    };
  });

  return () => {
    settle();
    mm.revert();
  };
}
